import React, { useRef } from "react";
import "../css/chatMessage.css";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkSlug from "remark-slug";
import remarkAutolinkHeadings from "remark-autolink-headings";
import rehypeRaw from "rehype-raw";
import TableWithCopy from "./markdown/TableWithCopy";
import CodeBlockWithCopy from "./markdown/CodeBlockWithCopy";
import InlineCode from "./markdown/InlineCode";
import ImageWithZoom from "./markdown/ImageWithZoom";
import VideoWithZoom from "./markdown/VideoWithZoom";
import YouTubeEmbed from "./markdown/YouTubeEmbed";
import { branding } from "../utils/branding";
import fileIcon from "../assets/UserIcons/FileIcon.svg";
import downloadIcon from "../assets/UserIcons/DownloadIcon.svg";
import cpyIcon from "../assets/UserIcons/Copy.svg";
import knowledgeBaseIcon from "../assets/UserIcons/knowledgeBase.svg";
import linkIcon from "../assets/UserIcons/weblink.svg";
import globalIcon from "../assets/UserIcons/global.svg";
import reloadIcon from "../assets/UserIcons/Reload.svg";
import speakerIcon from "../assets/UserIcons/Speaker.svg";
import onlineIcon from "../assets/UserIcons/OnlineMode.svg";
import offlineIcon from "../assets/UserIcons/OfflineModeBlack.svg";
import tokenIcon from "../assets/UserIcons/token_icon.svg";
import Mermaid from "./markdown/Mermaid";
import { useSelector } from "react-redux";
import { useEffect, useState } from "react";
import arrow from "../assets/icons/Arrow left.svg";

// ============================================================================
// RELATED QUESTIONS HELPERS
// ============================================================================
const sanitizeRelatedQuestion = (text = "") => {
  if (typeof text !== "string") return "";

  return (
    text
      // remove markdown bold/italic
      .replace(/\*{1,3}/g, "")

      // remove unicode dashes & bullets
      .replace(/^[\s—–•\-→➜]+/g, "")

      // remove numbered or bullet lists (1. 2) - *)
      .replace(/^\s*(\d+[\.)]|[-*+])\s+/g, "")

      // remove headings
      .replace(/^#+\s*/g, "")

      // normalize spaces
      .replace(/\s+/g, " ")
      .trim()
  );
};

const extractRelatedQuestions = (text = "") => {
  if (!text || typeof text !== "string") {
    return { cleanText: text, relatedQuestions: [] };
  }

  const patterns = [
    /brief\s+follow[-\s]?up(?: questions?| suggestions?)?:?/i,
    /follow[-\s]?up(?: questions?| suggestions?)?:?/i,
    /related questions?:?/i,
    /suggested questions?:?/i,
    /you may also ask:?/i,
  ];

  let matchIndex = -1;
  let matchLength = 0;

  for (const pattern of patterns) {
    const match = text.match(pattern);
    if (match) {
      matchIndex = match.index;
      matchLength = match[0].length;
      break;
    }
  }

  // If no follow-up section found
  if (matchIndex === -1) {
    // fallback: bold question at end
    const boldMatch = text.match(/\*\*(.+\?)\*\*\s*$/);
    if (boldMatch) {
      return {
        cleanText: text.replace(boldMatch[0], "").trim(),
        // relatedQuestions: [boldMatch[1]],
        relatedQuestions: [sanitizeRelatedQuestion(boldMatch[1])],
      };
    }

    return { cleanText: text, relatedQuestions: [] };
  }

  const mainText = text.slice(0, matchIndex).trim();
  const followUpText = text.slice(matchIndex + matchLength).trim();

  const questions = followUpText
    .split(/[\n•\-—]+/)
    .map((line) => sanitizeRelatedQuestion(line))
    .filter((q) => q && q.endsWith("?"));

  return {
    cleanText: mainText,
    relatedQuestions: [...new Set(questions)],
  };
};

// ============================================================================
// SOURCES HELPERS
// ============================================================================

const stripSourcesFromMarkdown = (markdown = "") => {
  return markdown
    .replace(/(---\s*)?(\*\*|\#\#\#)\s*(🔗\s*)?sources?:?[\s\S]*$/i, "")
    .trim();
};

const extractSourcesFromMarkdown = (markdown = "") => {
  if (typeof markdown !== "string") return [];

  const urls = markdown.match(/https?:\/\/[^\s)]+/g) || [];
  return [...new Set(urls)];
};

const resolveSources = (msg) => {
  if (!msg) return [];

  // 1️⃣ Prefer backend citations
  if (Array.isArray(msg.citations) && msg.citations.length > 0) {
    return [...new Set(msg.citations)];
  }

  // 2️⃣ Fallback: extract from markdown response
  if (typeof msg.message === "string") {
    return extractSourcesFromMarkdown(msg.message);
  }

  return [];
};

const normalizeUrl = (url) => {
  if (!url) return "#";
  return url.startsWith("http") ? url : `https://${url}`;
};

const getDomain = (url) => {
  try {
    return new URL(normalizeUrl(url)).hostname.replace("www.", "");
  } catch {
    return url;
  }
};

// ============================================================================
// MEDIA DETECTION UTILITIES
// ============================================================================

const MEDIA_PATTERNS = {
  // Images: .png, .jpg, .jpeg, .gif, .webp (with optional query params)
  image: /(https?:\/\/[^\s]+?\.(png|jpg|jpeg|gif|webp))(\?[^\s]*)*/i,
  // Videos: .mp4, .webm, .ogg (with optional query params)
  video: /(https?:\/\/[^\s]+?\.(mp4|webm|ogg))(\?[^\s]*)*/i,
};

/**
 * Detect media type from URL
 * @param {string} url - The URL to check
 * @returns {string|null} - 'image', 'video', or null
 */
const detectMediaType = (url) => {
  if (!url || typeof url !== "string") return null;

  const trimmed = url.trim();

  if (MEDIA_PATTERNS.video.test(trimmed)) return "video";
  if (MEDIA_PATTERNS.image.test(trimmed)) return "image";

  return null;
};

/**
 * Extract media URL from markdown-wrapped content
 * @param {string} content - Raw markdown content
 * @returns {string|null} - Extracted URL or null
 */
const extractMediaUrl = (content) => {
  if (!content || typeof content !== "string") return null;

  let url = content.trim();

  // Remove markdown image syntax: ![alt](url)
  if (url.startsWith("![")) {
    url = url.replace(/^!\[.*?\]\(/, "").replace(/\)$/, "");
  }

  // Remove markdown link syntax: [text](url)
  if (url.startsWith("[") && url.includes("](")) {
    url = url.replace(/^\[.*?\]\(/, "").replace(/\)$/, "");
  }

  // Remove surrounding quotes
  url = url.replace(/^["']|["']$/g, "");

  // Decode HTML entities
  url = url.replace(/&amp;/g, "&").replace(/&quot;/g, '"');

  return url.trim();
};

/**
 * Process message content to detect and wrap ONLY OpenAI DALL·E image URLs
 * @param {string} message - The message content
 * @returns {string} - Processed message with OpenAI images wrapped in markdown
 */
const processMessageForMedia = (message) => {
  if (!message || typeof message !== "string") return message;

  let processed = message;

  // ✅ Only allow OpenAI DALL·E Azure Blob images
  const OPENAI_IMAGE_DOMAIN =
    "https://oaidalleapiprodscus.blob.core.windows.net/";

  // 1️⃣ Convert markdown links pointing to OpenAI image → image markdown
  processed = processed.replace(
    /\[([^\]]*)\]\((https:\/\/oaidalleapiprodscus\.blob\.core\.windows\.net\/[^\s)]+)\)/gi,
    "![]($2)",
  );

  // 2️⃣ Convert plain OpenAI image URLs → image markdown
  processed = processed.replace(
    /(https:\/\/oaidalleapiprodscus\.blob\.core\.windows\.net\/[^\s<>"']+)/gi,
    "![]($1)",
  );

  return processed;
};

const SourcesPill = ({ sources }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  // ⬇️ Close on outside click
  useEffect(() => {
    if (!open) return;

    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  if (!sources || sources.length === 0) return null;

  return (
    <div className="sources-container" ref={containerRef}>
      <button className="sources-pill" onClick={() => setOpen((prev) => !prev)}>
        <span className="icon">🌐</span>
        <span className="icon-badge">{sources.length}</span>
        {/* 🔗 <span className="web-text">{sources.length}</span> */}
      </button>

      {/* <img
        src={globalIcon}
        alt="Source Link"
        className="icon-image"
        role="button"
        title="Source Link"
        onClick={() => setOpen((prev) => !prev)}
        style={{
          width: "18px",
          height: "18px",
          cursor: "pointer",
        }}
      /> */}

      {open && (
        <div className="sources-popover overflow-hidden">
          <div className="sources-title">Web Search Source:</div>
          <ul>
            {sources.map((src, i) => (
              <li
                title={normalizeUrl(src)}
                key={i}
                className="flex-warp w-100 text-truncate mb-1 sources-items-list"
              >
                <a
                  href={normalizeUrl(src)}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {getDomain(src)}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

function sanitizeMarkdown(md = "") {
  return (
    md
      // Remove trailing bold markers
      .replace(/\*{2,}\s*$/g, "")
      // Remove trailing list markers
      .replace(/^\s*[-*+]\s*$/gm, "")
      // Remove trailing blockquote markers
      .replace(/^\s*>\s*$/gm, "")
      // Collapse excessive newlines
      .replace(/\n{3,}/g, "\n\n")
      .trim()
  );
}

// ============================================================================
// RAG SOURCES COMPONENT
// ============================================================================

const sanitizeFilename = (name = "") => {
  if (typeof name !== "string") return "";

  /*return name
    .replace(/^https?:\/\//i, "") // remove protocol
    .replace(/[#?].*$/, "") // remove query/hash
    .split("/")
    .pop()
    .replace(/\s+/g, " ")
    .trim();*/

  return name
    .replace(/^https?:\/\//i, "") // remove protocol
    .replace(/^http?:\/\//i, "") // remove protocol
    .replace(/^www?:\/\//i, "") // remove protocol
    .replace(/\/.*$/, "") // remove everything after first /
    .replace(/[#?].*$/, "") // remove query/hash if any
    .trim();
};

const handleViewFile = (document_id, originalFileName = "") => {
  if (!document_id) return;

  const fileUrl = `${process.env.REACT_APP_API_URL}/uploaded-files/viewSources?document_id=${document_id}`;

  const extension = originalFileName?.includes(".")
    ? originalFileName.split(".").pop().toLowerCase()
    : "";
  console.log("extension", extension);

  const imageExtensions = ["png", "jpg", "jpeg", "webp", "gif"];

  // 🖼 Images → open directly
  if (imageExtensions.includes(extension)) {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  // 📕 PDF → browser native viewer
  if (extension === "pdf") {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
    return;
  }

  // 📄 DOC / DOCX / TXT / etc → Google Docs Viewer
  const googleViewUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(
    fileUrl,
  )}&embedded=true`;

  try {
    window.open(googleViewUrl, "_blank", "noopener,noreferrer");
  } catch {
    window.open(fileUrl, "_blank", "noopener,noreferrer");
  }
};

const RagSourcesPill = ({ ragSources = [] }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  if (!Array.isArray(ragSources) || ragSources.length === 0) return null;

  // ✅ keep full source object (dedupe by document_id)
  const sources = [
    ...new Map(
      ragSources
        .filter((s) => s?.filename && s?.document_id)
        .map((s) => [s.document_id, s]),
    ).values(),
  ];

  if (sources.length === 0) return null;

  return (
    <div className="sources-container" ref={ref}>
      <button className="sources-pill" onClick={() => setOpen((p) => !p)}>
        <span className="icon">📚</span>
        <span className="icon-badge">{sources.length}</span>
      </button>

      {open && (
        <div className="sources-popover">
          <div className="sources-title">Knowledge Base Source:</div>
          <ul>
            {sources.map((source) => (
              <li
                key={source.document_id}
                className="sources-items-list text-truncate mb-1"
                title="Click to view file"
                onClick={() =>
                  handleViewFile(source.document_id, source.filename)
                }
                style={{
                  cursor: "pointer",
                  color: "#0d6efd",
                }}
              >
                📄 {source.filename}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// RELATED QUESTIONS COMPONENT
// ============================================================================

const RelatedQuestions = ({ followUp = [], onSelect }) => {
  if (!Array.isArray(followUp) || followUp.length === 0) return null;

  const questions = [
    ...new Set(
      followUp
        .map((q) => sanitizeRelatedQuestion(q))
        .filter((q) => q && q.endsWith("?")),
    ),
  ];

  if (questions.length === 0) return null;

  return (
    <div className="related-questions">
      <div className="related-title">Related questions</div>
      <div className="related-list">
        {questions.map((q, i) => (
          <button
            key={i}
            className="related-pill"
            onClick={() => onSelect?.(q)}
          >
            {q}
          </button>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const ChatMessage = React.memo(
  ({
    msg,
    loggedInPic,
    userName,
    fontSize,
    onCopy,
    onRetry,
    onTextToSpeech,
    onRelatedQuestionSend,
    searchActive,
    onUpdateMessage,
    botBusy,
  }) => {
    useEffect(() => {
      if (msg.sender !== "bot" || !msg.startedAt || msg.response_record_time)
        return;

      const interval = setInterval(() => {
        const elapsed = (Date.now() - msg.startedAt) / 1000;

        const liveTime = Number(elapsed.toFixed(2));

        onUpdateMessage(msg.id, { liveResponseTime: liveTime });
      }, 100);

      return () => clearInterval(interval);
    }, [msg.startedAt, msg.response_record_time, msg.id, onUpdateMessage]);

    const isBot = msg.sender === "bot";
    const isTyping = Boolean(msg.partialMessage); // ✅ ADD THIS
    const [relatedQuestions, setRelatedQuestions] = useState([]);

    const botGeneratedFile = isBot ? msg.bot_generated_file : null;
    const generatedFileName =
      botGeneratedFile?.name ||
      (botGeneratedFile?.url
        ? decodeURIComponent(
            botGeneratedFile.url.split("?")[0].split("/").pop() || "generated_file",
          )
        : "generated_file");
    const generatedFileSizeLabel =
      typeof botGeneratedFile?.size === "number" && Number.isFinite(botGeneratedFile.size)
        ? botGeneratedFile.size >= 1024 * 1024
          ? `${(botGeneratedFile.size / (1024 * 1024)).toFixed(2)} MB`
          : `${(botGeneratedFile.size / 1024).toFixed(2)} KB`
        : null;
    const generatedFileDescription =
      typeof botGeneratedFile?.description === "string" && botGeneratedFile.description.trim()
        ? botGeneratedFile.description.trim()
        : null;

    const profile =
      msg.sender === "bot"
        ? "/img/VelaKoda_Fevicon.svg"
        : msg.userPic || loggedInPic;
    const displayName =
      msg.sender === "bot"
        ? branding("ZeroBox")
        : msg.userName || userName || "User";

    const analyzingMessage = "Analyzing the request";
    const understandingUserMessage = "Understanding user intent";
    const interpretingUserContextMessage = "Interpreting user context";
    const evaluatingMessage = "Evaluating response approach";
    const preparingMessage = "Preparing response strategy";
    const formulatingMessage = "Formulating response plan";
    const thinkingMessage = "Thinking";
    const checkingKnowledgeBaseMessage = "Checking knowledge base";
    const webMessage = "Checking web sources";
    const crossInfoMessage = "Cross-checking references";
    const validatingInfoMessage = "Validating information";
    const organizatingInfoMessage = "Organizing response insights";
    const ensuringCRMessage = "Ensuring clarity and relevance";
    const refiningResponseQualityMessage = "Refining response quality";
    const filteringInfoMessage = "Filtering relevant information";
    const maintainingConversationalToneMessage =
      "Maintaining conversational tone";
    const determiningMessage = "Determining best response";
    const structuringMessage = "Structuring response";
    const finalMessage = "Generating response";

    const baseMessages = [
      analyzingMessage,
      understandingUserMessage,
      interpretingUserContextMessage,
      evaluatingMessage,
      preparingMessage,
      formulatingMessage,
      checkingKnowledgeBaseMessage,
      crossInfoMessage,
      validatingInfoMessage,
      organizatingInfoMessage,
      ensuringCRMessage,
      refiningResponseQualityMessage,
      filteringInfoMessage,
      maintainingConversationalToneMessage,
      determiningMessage,
      structuringMessage,
    ];

    const messages = searchActive
      ? [...baseMessages, webMessage, finalMessage]
      : [...baseMessages, finalMessage];

    const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
    const [displayMessage, setDisplayMessage] = useState(messages[0]);

    const isDefaultMessage = messages.includes(displayMessage);
    useEffect(() => {
      if (currentMessageIndex >= messages.length - 1) return;

      const timer = setTimeout(() => {
        setCurrentMessageIndex((prev) => prev + 1);
        setDisplayMessage(messages[currentMessageIndex + 1]);
      }, 3000); // ⬅️ 3 seconds per step

      return () => clearTimeout(timer);
    }, [currentMessageIndex, messages]);

    useEffect(() => {
      setCurrentMessageIndex(0);
      setDisplayMessage(messages[0]);
    }, [searchActive]);

    useEffect(() => {
      if (!isBot || msg.showDots) return;

      // 1️⃣ Prefer backend follow_up (already array)
      if (Array.isArray(msg.follow_up) && msg.follow_up.length > 0) {
        const sanitized = [
          ...new Set(
            msg.follow_up
              .map((q) => sanitizeRelatedQuestion(q))
              .filter((q) => q && q.endsWith("?")),
          ),
        ];

        setRelatedQuestions(sanitized);
        return;
      }

      // 2️⃣ Fallback: extract from message text
      if (typeof msg.message === "string") {
        const extracted = extractRelatedQuestions(msg.message);
        setRelatedQuestions(extracted.relatedQuestions || []);
      }
    }, [msg.message, msg.follow_up, msg.showDots]);

    return (
      <div
        className={`d-flex ${
          isBot ? "flex-row" : "flex-row-reverse"
        } align-items-start my-4`}
      >
        <div className="d-flex flex-column chat-message-container">
          <div
            className={`chat-bubble ${isBot ? "bot-bubble" : "user-bubble"}`}
          >
            <div className="d-flex align-items-center gap-2 mb-2">
              <img
                src={profile}
                className={isBot ? "bot-avatar" : "chat-avatar"}
                alt="avatar"
              />
              <strong>{displayName}</strong>
              <small>{msg.time}</small>

              {isBot && (
                <small style={{ fontStyle: "italic", color: "#888" }}>
                  (
                  {Number(
                    msg.response_record_time ??
                      msg.liveResponseTime ??
                      msg.response_time,
                  )?.toFixed(2) || "0.00"}{" "}
                  )
                </small>
              )}
            </div>

            <div className="formatted-text" style={{ fontSize }}>
              {msg.files && msg.files.length > 0 && (
                <div className="attached-files">
                  {msg.files.map((file, idx) => (
                    <div
                      className="file-bubble d-flex align-items-center gap-2 p-2 rounded"
                      key={idx}
                    >
                      <img src={fileIcon} alt="File" width="22" />
                      <div className="d-flex flex-column">
                        <small title={file.name} className="file-name">
                          {file.name}
                        </small>
                        <small className="file-size">
                          ({(file.size / 1024).toFixed(1)} KB)
                        </small>
                      </div>
                    </div>
                  ))}
                </div>
              )}


              {isBot && botGeneratedFile?.url && (
                <div className="generated-file-card d-flex align-items-center justify-content-between p-2 rounded mt-2">
                  <div className="d-flex align-items-center gap-2 overflow-hidden">
                    <img src={fileIcon} alt="Generated File" width="20" />
                    <div className="d-flex flex-column overflow-hidden">
                      <small className="file-name text-truncate" title={generatedFileName}>
                        {generatedFileName}
                      </small>
                      <small className="file-size">
                        {generatedFileSizeLabel || "FIle Size"}
                      </small>
                    </div>
                  </div>

                  <a
                    href={botGeneratedFile.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    download={generatedFileName}
                    className="generated-file-download"
                    title="Download file"
                  >
                    <img src={downloadIcon} alt="Download" width="18" height="18" />
                  </a>
                </div>
              )}
              {isBot && msg.showDots ? (
                // <div className="d-flex align-items-center message-animate">
                <div
                  key={currentMessageIndex}
                  className={`d-flex align-items-center ${
                    currentMessageIndex % 1 === 0 ? "message-animate" : ""
                  }`}
                >
                  <span
                    style={{
                      fontSize: isDefaultMessage ? "13px" : "",
                      opacity: 0.9,
                      fontStyle: "italic",
                    }}
                    className="me-1"
                  >
                    {displayMessage}
                  </span>
                  <div className="typing-indicator d-flex">
                    <span className="dot"></span>
                    <span className="dot"></span>
                    <span className="dot"></span>
                  </div>
                </div>
              ) : isBot ? (
                <div className="markdown-renderer">
                  <ReactMarkdown
                    remarkPlugins={[
                      remarkGfm,
                      remarkSlug,
                      remarkAutolinkHeadings,
                    ]}
                    rehypePlugins={[]} // keep rehypeRaw 
                    components={{
                      table: ({ node, ...props }) => (
                        <TableWithCopy {...props} />
                      ),

                      a: ({ href, children, ...props }) => {
                        if (
                          href &&
                          (href.includes("youtube.com") ||
                            href.includes("youtu.be"))
                        ) {
                          return <YouTubeEmbed url={href} />;
                        }

                        if (href && detectMediaType(href) === "video") {
                          return <VideoWithZoom src={href} />;
                        }

                        return (
                          <a
                            href={href}
                            target="_blank"
                            rel="noopener noreferrer"
                            {...props}
                          >
                            {children}
                          </a>
                        );
                      },

                      code({ inline, className, children }) {
                        const match = /language-(\w+)/.exec(className || "");
                        if (!inline && match) {
                          return (
                            <CodeBlockWithCopy
                              language={match[1]}
                              value={String(children).replace(/\n$/, "")}
                            />
                          );
                        }

                        if (inline) {
                          return <InlineCode>{children}</InlineCode>;
                        }

                        return <code>{children}</code>;
                      },

                      img: (props) => <ImageWithZoom {...props} />,

                      li: ({ children, ...props }) => {
                        const text = String(children).replace(/\s/g, "");
                        if (!text) return null;
                        return <li {...props}>{children}</li>;
                      },
                    }}
                  >
                    {(() => {
                      // 🔥 Choose correct source
                      let content = isTyping
                        ? msg.partialMessage // streaming text
                        : msg.message; // final text

                      if (typeof content === "string") {
                        // ✅ NO follow-up extraction here anymore
                        content = processMessageForMedia(content);
                        content = stripSourcesFromMarkdown(content);
                        content = sanitizeMarkdown(content);
                      }

                      return content;
                    })()}
                  </ReactMarkdown>
                </div>
              ) : (
                // 👤 USER → Preserve textarea formatting
                <div
                  className="formatted-text"
                  style={{
                    fontSize,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {msg.message}
                </div>
              )}
            </div>
          </div>

          {!msg.showDots && (
            <div
              className={`d-flex bot-below-icon gap-2 gap-md-3 align-items-center mt-1 ${
                isBot
                  ? "justify-content-start ms-3"
                  : "justify-content-end me-3 "
              }`}
            >
              {isBot ? (
                <>
                  <img
                    src={speakerIcon}
                    alt="Play Sound"
                    className="icon-image"
                    role="button"
                    title="Play Sound"
                    onClick={() => onTextToSpeech(msg.message)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />

                  <img
                    src={cpyIcon}
                    className="icon-image"
                    alt="Copy"
                    role="button"
                    title="Copy"
                    onClick={() => onCopy(msg.message)}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                    }}
                  />

                  <img
                    src={reloadIcon}
                    className="icon-image pointer"
                    alt="Retry"
                    role="button"
                    title="Retry"
                    onClick={() => onRetry(msg.id)}
                    style={{ width: "16px", height: "16px" }}
                  />

                  {/* <img
                    src={reloadIcon}
                    className={`icon-image pointer ${
                      botBusy ? "disabled" : ""
                    }`}
                    alt="Retry"
                    title={botBusy ? "Bot is responding" : "Retry"}
                    onClick={() => {
                      if (!botBusy) onRetry(msg.id);
                    }}
                    style={{ width: "16px", height: "16px" }}
                  /> */}

                  {!msg.showDots && (
                    <SourcesPill sources={resolveSources(msg) || []} />
                  )}

                  {!msg.showDots && (
                    <RagSourcesPill ragSources={msg.rag_sources} />
                  )}

                  {msg.model_generic_name && (
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={
                          msg?.model_type.toLowerCase() == "online"
                            ? onlineIcon
                            : offlineIcon
                        }
                        alt="mode-icon"
                        style={{ width: "20px", height: "18px" }}
                        className="icon-image"
                      />
                      <small
                        style={{
                          fontSize: "0.8rem",
                          color: "var(--text-muted, #888)",
                          fontStyle: "italic",
                        }}
                      >
                        {msg.model_generic_name}
                      </small>
                    </div>
                  )}

                  {/* VKD122 🔹 TOKEN + RESPONSE TIME INFO */}
                  {(msg.response_time ||
                    msg.input_tokens ||
                    msg.output_tokens) && (
                    <div
                      className="d-flex align-items-center gap-2 ms-2"
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--text-muted, #888)",
                        fontStyle: "italic",
                      }}
                    >
                      {/* {msg.response_time && (
                        <span>⏱ {Number(msg.response_time).toFixed(2)}s</span>
                      )} */}

                      {msg.input_tokens && msg.output_tokens && (
                        <div className="d-flex align-items-center gap-1">
                          <img
                            src={tokenIcon}
                            alt="Tokens"
                            title="Token Usage"
                            className="icon-image"
                            style={{
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          <span>
                            {msg.input_tokens} → {msg.output_tokens}
                          </span>
                        </div>
                      )}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <img
                    src={cpyIcon}
                    className="icon-image pointer"
                    alt="Copy"
                    role="button"
                    title="Copy"
                    onClick={() => onCopy(msg.message)}
                    style={{ width: "16px", height: "16px" }}
                  />
                </>
              )}
            </div>
          )}
          {/* {isBot && !msg.showDots && relatedQuestions.length > 0 && (
            <div className="related-card mt-2">
              <div className="related-header formatted-text">
                Related Questions
              </div>

              <ul className="related-list">
                {relatedQuestions.map((q, i) => (
                  <li
                    key={i}
                    className="related-item p-1 px-md-2 py-md-1"
                    onClick={() => onRelatedQuestionSend?.(q)}
                  >
                    <img
                      className="related-arrow icon-image"
                      style={{ height: "16px", width: "16px" }}
                      src={arrow}
                      alt="arrow"
                    />
                    <span className="related-text ">{q}</span>
                  </li>
                ))}
              </ul>
            </div>
          )} */}
        </div>
      </div>
    );
  },
  (prevProps, nextProps) => {
    // Custom comparison - only re-render if these specific properties change
    return (
      prevProps.msg.id === nextProps.msg.id &&
      prevProps.msg.message === nextProps.msg.message &&
      prevProps.msg.partialMessage === nextProps.msg.partialMessage &&
      prevProps.msg.showDots === nextProps.msg.showDots &&
      prevProps.msg.model_generic_name === nextProps.msg.model_generic_name &&
      prevProps.msg.liveResponseTime === nextProps.msg.liveResponseTime && // ✅ ADD THIS
      prevProps.msg.response_record_time ===
        nextProps.msg.response_record_time &&
      prevProps.msg.bot_generated_file?.url ===
        nextProps.msg.bot_generated_file?.url &&
      prevProps.fontSize === nextProps.fontSize
    );
  },
);

export default ChatMessage;
